import { assign, createMachine, interpret, Interpreter } from 'xstate-v4'
import { MeInfo, SocialLoginResponse } from './types'

export type AuthEvent =
  | { type: 'LOG_IN_EMAIL'; langCode: string }
  | { type: 'LOG_IN_FACEBOOK'; token: string; next?: string }
  | { type: 'LOG_IN_GOOGLE'; token: string; next?: string }
  | { type: 'LOG_OUT' }
  | { type: 'LOGGED_IN_ANOTHER_TAB' }
  | { type: 'LOGGED_OUT_ANOTHER_TAB' }
  | { type: 'CHANGE_EMAIL'; value: string }
  | { type: 'CHANGE_PASSWORD'; value: string }

type FormErrors = { message?: string; formError?: string }
function getAuthContext() {
  return {
    email: '',
    socialToken: '',
    password: '',
    error: [] as string[] | FormErrors,
    isManager: false,
    isSuperuser: false,
    isStaff: false,
    next: '',
  }
}

export type AuthContext = ReturnType<typeof getAuthContext>
export type AuthMachineInterpreter = Interpreter<AuthContext, any, AuthEvent>

// tslint:disable no-duplicate-string
export function getAuthMachinePure() {
  return createMachine<AuthContext, AuthEvent>(
    {
      id: 'auth',
      initial: 'initialising',
      context: getAuthContext(),
      states: {
        initialising: {
          invoke: {
            src: 'getMeInfo',
            onDone: [
              {
                cond: 'isMeInfoAuthorized',
                target: '#auth.loggedIn',
                actions: ['assignMeInfoEmail'],
                internal: true,
              },
              { target: '#auth.loggedOut.normal', internal: true },
            ],
            onError: {
              target: '#auth.loggedOut.normal',
              actions: ['logError'],
              internal: true,
            },
          },
        },
        loggedOut: {
          on: {
            LOG_IN_EMAIL: 'loggingIn.email',
            CHANGE_EMAIL: {
              actions: assign({
                email: (_, event) => event.value,
              }),
              internal: true,
            },
            CHANGE_PASSWORD: {
              actions: assign({
                password: (_, event) => event.value,
              }),
              internal: true,
            },
            LOG_IN_FACEBOOK: {
              target: 'loggingIn.facebook',
              actions: assign({
                next: (_, event) => event.next || '',
              }),
            },
            LOG_IN_GOOGLE: {
              target: 'loggingIn.google',
              actions: assign({
                next: (_, event) => event.next || '',
              }),
            },
          },
          initial: 'verifying',
          states: {
            normal: {
              entry: assign((_) => ({
                error: [] as string[] | FormErrors,
                email: '',
                password: '',
              })),
            },
            error: {},
            verifying: {
              invoke: {
                src: 'getMeInfo',
                onDone: [
                  {
                    cond: 'isMeInfoAuthorized',
                    target: '#auth.loggedIn',
                    actions: 'assignMeInfoEmail',
                  },
                  { target: 'normal' },
                ],
                onError: {
                  target: 'normal',
                  actions: ['logError'],
                  internal: true,
                },
              },
            },
          },
        },
        loggingIn: {
          entry: assign((_) => ({
            error: [] as string[] | FormErrors,
          })),
          exit: assign((_) => ({
            socialToken: '',
          })),
          states: {
            email: {
              invoke: {
                src: 'submitEmailLogin',
                onDone: {
                  target: '#auth.fetchingProfile',
                  actions: [
                    assign({
                      password: (_) => '',
                    }),
                  ],
                },
                onError: [
                  {
                    target: '#auth.loggedOut.error',
                    actions: ['setError'],
                  },
                  {
                    target: '#auth.loggedOut.error',
                    actions: ['logError'],
                  },
                ],
              },
            },
            facebook: {
              entry: assign({
                socialToken: (_, event) =>
                  event.type === 'LOG_IN_FACEBOOK' ||
                  event.type === 'LOG_IN_GOOGLE'
                    ? event.token
                    : '',
              }),
              invoke: {
                src: 'submitFacebookLogin',
                data: (context: AuthContext) => ({
                  socialToken: context.socialToken,
                  next: context.next,
                }),
                onDone: {
                  target: '#auth.fetchingProfile',
                  actions: [
                    assign({
                      email: (_, { data }) => data.email,
                    }),
                  ],
                },
                onError: {
                  actions: ['logError'],
                  target: '#auth.loggedOut',
                },
              },
            },
            google: {
              entry: assign({
                socialToken: (_, event) =>
                  event.type === 'LOG_IN_FACEBOOK' ||
                  event.type === 'LOG_IN_GOOGLE'
                    ? event.token
                    : '',
              }),
              invoke: {
                src: 'submitGoogleLogin',
                data: (context: AuthContext) => ({
                  socialToken: context.socialToken,
                  next: context.next,
                }),
                onDone: {
                  target: '#auth.fetchingProfile',
                  actions: [
                    assign({
                      email: (_, { data }) => data.email,
                    }),
                  ],
                },
                onError: {
                  actions: ['logError'],
                  target: '#auth.loggedOut',
                },
              },
            },
          },
        },
        fetchingProfile: {
          invoke: {
            src: 'getMeInfo',
            onDone: {
              target: '#auth.loginSuccess',
              actions: ['assignMeInfoEmail'],
            },
            onError: {
              target: '#auth.loginSuccess',
              actions: ['logError'],
            },
          },
        },
        loggingOut: {
          invoke: {
            src: 'submitLogout',
            onDone: {
              target: '#auth.logoutSuccess',
              actions: [
                assign({
                  email: (_) => '',
                  socialToken: (_) => '',
                  password: (_) => '',
                }),
              ],
            },
            onError: {
              target: '#auth.logoutSuccess',
              actions: ['logError'],
            },
          },
        },
        logoutSuccess: {
          // For some reason xstate-v4s `after` is not working
          invoke: {
            src: 'delay100',
            onDone: '#auth.loggedOut',
          },
        },
        loginSuccess: {
          // For some reason xstate-v4s `after` is not working
          invoke: {
            src: 'handleLoginSuccess',
            data: (context: AuthContext) => context,
            onDone: '#auth.loggedIn',
          },
        },
        loggedIn: {
          on: {
            LOG_OUT: 'loggingOut',
            LOGGED_OUT_ANOTHER_TAB: 'loggedOut.normal',
            CHANGE_EMAIL: {
              actions: assign({
                email: (_, { value }) => value,
              }),
              internal: true,
            },
          },
        },
      },
    },
    {
      actions: {
        assignMeInfoEmail: assign({
          email: (_, { data }: any) => data.email,
          isManager: (_, { data }: any) => data.isManager || false,
          isSuperuser: (_, { data }: any) => data.isSuperuser || false,
          isStaff: (_, { data }: any) => data.isStaff || false,
        }),
        clearEmail: assign({
          email: (_) => '',
        }),
        setError: assign({
          error: (_, { data }: any) => {
            if (data.formError || data.message) {
              return data
            }
            if (Array.isArray(data.message)) {
              return data.message
            }
            // Handle single message
            return [data.message]
          },
        }),
        setUnknownError: assign({ error: (_) => ['Unknown error happened'] }),
        logError: () => {
          throw new Error('authMachine logError not implemented')
        },
      },
      guards: {
        isMeInfoAuthorized: (_, event: any) =>
          event.data.status === 'authorized',
      },
      services: {
        delay100: async (_) =>
          await new Promise((resolve) => {
            setTimeout(resolve, 100)
          }),
        handleLoginSuccess: () => {
          throw new Error('handleLoginSuccess not implemented')
        },
        getMeInfo: () => {
          throw new Error('getMeInfo not implemented')
        },
        submitEmailLogin: () => {
          throw new Error('submitEmailLogin not implemented')
        },
        submitFacebookLogin: () => {
          throw new Error('submitFacebookLogin not implemented')
        },
        submitGoogleLogin: () => {
          throw new Error('submitGoogleLogin not implemented')
        },
        submitLogout: () => {
          throw new Error('submitLogout not implemented')
        },
      },
    },
  )
}

interface BuilderServices {
  getMeInfo: () => Promise<MeInfo>
  submitEmailLogin: (
    params: {
      email: string
      password: string
    },
    event: any,
  ) => Promise<any>
  submitFacebookLogin: (params: {
    socialToken: string
    next?: string
  }) => Promise<SocialLoginResponse>
  submitGoogleLogin: (params: {
    socialToken: string
    next?: string
  }) => Promise<SocialLoginResponse>
  submitLogout: () => Promise<void>
  handleLoginSuccess: (context: AuthContext) => Promise<void>
}

export function buildAuthMachine(services: BuilderServices, actions: any) {
  if (import.meta.server) {
    services = {
      ...services,
      getMeInfo: async () => await Promise.resolve({ status: 'unauthorized' }),
    }
  }
  return interpret(
    getAuthMachinePure().withConfig({
      services: { ...services },
      actions: {
        ...actions,
      },
    }),
  )
}
